import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface UseQueryParamsProps {
  defaultSort?: string;
  defaultDirection?: 'asc' | 'desc';
}

export const useQueryParams = ({ defaultSort, defaultDirection = 'asc' }: UseQueryParamsProps) => {
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = searchParams.get('sort') || defaultSort;
  const direction = searchParams.get('direction') || defaultDirection;
  const page = searchParams.get('page');
  const keyword = searchParams.get('keyword');

  const handleSort = (key: string) => {
    const newDirection = sort === key && direction === 'asc' ? 'desc' : 'asc';
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('sort', key);
    newSearchParams.set('direction', newDirection);
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (newPage: number) => {
    const query = new URLSearchParams(location.search);
    query.set('page', newPage.toString());
    navigate({ search: query.toString() });
  };

  const handleSearch = () => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('keyword', searchValue || '');
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClearParams = () => {
    const newSearchParams = new URLSearchParams();
    setSearchParams(newSearchParams);
  };

  const getSearchParams = () => ({
    sort: sort || undefined,
    direction: direction || undefined,
    page: page || undefined,
    keyword: keyword || undefined,
  });

  return {
    searchValue,
    setSearchValue,
    handleSort,
    handlePageChange,
    handleSearch,
    handleSearchKeyDown,
    handleClearParams,
    getSearchParams,
    sort,
    direction,
    page,
    keyword,
  };
};
